/**
 * Production Firebase Service Implementation
 *
 * Uses Firebase SDK for optimal performance and full feature support.
 * This implementation is used in production and development environments.
 */

import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { getAuth, getIdToken } from 'firebase/auth';
import app from '../firebase';
import type { FirebaseService, FirebaseConfig, FirebaseServiceError } from './firebase-service';

// Build-time assertion to prevent wrong-environment bundling
if (import.meta.env.MODE === 'staging') {
  throw new Error('Production Firebase service loaded in staging build - check Vite configuration');
}

export class ProductionFirebaseService implements FirebaseService {
  private functions: Functions;
  private config: FirebaseConfig;

  constructor() {
    const region = (import.meta.env.VITE_FIREBASE_REGION as string) || 'us-central1';
    this.config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      region,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN
    };

    try {
      this.functions = getFunctions(app, region);
    } catch (error: any) {
      console.error('❌ Production Firebase Functions initialization failed:', error);
      throw new Error(`Failed to initialize Firebase Functions: ${error.message}`);
    }
  }

  async callFunction<T = any, R = any>(name: string, data?: T): Promise<{ data: R }> {
    try {
      const callable = httpsCallable<T, R>(this.functions, name);
      const result = await callable(data as T);
      return { data: result.data };
    } catch (error: any) {
      console.error(`❌ [PROD] Function ${name} failed:`, error);
      throw new Error(`Firebase Function ${name} failed: ${error.message}`);
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      await this.callFunction('ping', {});
      return true;
    } catch {
      return false;
    }
  }

  getServiceInfo() {
    return {
      type: 'sdk' as const,
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      region: this.config.region
    };
  }
}

// Export singleton instance
export const firebaseService = new ProductionFirebaseService();